import type { DashboardConfig, UpdateConfigRequest } from '../../api/index.ts';

/** The editable bp keys (tax_basis is server-fixed and rendered read-only).
 *  commission_rate_bp (E2.1 spec 7.3/7.6) rides the SAME bp field treatment
 *  as the tax fields -- same 0-10000 range, same percent hint -- so adding
 *  it here is the whole integration; ConfigPage.tsx needs no field-specific
 *  branch for it. */
export const BP_FIELDS = [
  'tax_rate_bp',
  'tax_split_ngcb_gaming_levy_bp',
  'tax_split_ngcb_lottery_dividend_bp',
  'tax_split_ngcb_community_benefit_bp',
  'tax_split_irc_bp',
  'commission_rate_bp',
] as const;

export type BpField = (typeof BP_FIELDS)[number];
/** device_monthly_fee_minor is entered/rendered as MAJOR units (2dp), unlike
 *  every other editable key here which is a plain integer -- see
 *  parseMajorMoneyToMinor/minorToMajorString below. */
export type EditableField = BpField | 'licence_validity_days' | 'device_monthly_fee_minor' | 'sync_endpoint_url';

export type ConfigFormState = Record<EditableField, string>;

export const CONFIG_SECTION_FIELDS = {
  taxation: [
    'tax_rate_bp',
    'tax_split_ngcb_gaming_levy_bp',
    'tax_split_ngcb_lottery_dividend_bp',
    'tax_split_ngcb_community_benefit_bp',
    'tax_split_irc_bp',
  ],
  billing: ['commission_rate_bp', 'device_monthly_fee_minor'],
  licensing: ['licence_validity_days'],
  sync: ['sync_endpoint_url'],
} as const satisfies Record<string, readonly EditableField[]>;

export type ConfigSection = keyof typeof CONFIG_SECTION_FIELDS;
type MappedEditableField = (typeof CONFIG_SECTION_FIELDS)[ConfigSection][number];
export type AssertTrue<T extends true> = T;
type EveryEditableFieldMapped = AssertTrue<[EditableField] extends [MappedEditableField] ? true : false>;

export const FIELD_LABELS: Record<EditableField, string> = {
  tax_rate_bp: 'Tax rate (bp)',
  tax_split_ngcb_gaming_levy_bp: 'NGCB Gaming Levy split (bp)',
  tax_split_ngcb_lottery_dividend_bp: 'NGCB Lottery Dividend split (bp)',
  tax_split_ngcb_community_benefit_bp: 'NGCB Community Benefit split (bp)',
  tax_split_irc_bp: 'IRC split (bp)',
  commission_rate_bp: '1NL commission rate (bp)',
  licence_validity_days: 'Licence validity (days)',
  device_monthly_fee_minor: 'Device monthly fee',
  sync_endpoint_url: 'Sync endpoint',
};

/** Server range for device_monthly_fee_minor (routes/config.ts's
 *  DEVICE_MONTHLY_FEE_MINOR_MAX): 100,000,000 minor units = 1,000,000.00
 *  major units/device/month. */
export const DEVICE_FEE_MAX_MINOR = 100_000_000;

/** Fixed 2dp major-units string from an integer minor amount, e.g.
 *  100000 -> "1000.00". Division + toFixed, never a trailing-zero-trimming
 *  regex (this file's sonar S5852 history). */
export function minorToMajorString(minor: number): string {
  return (minor / 100).toFixed(2);
}

// Whole major units, or major.d / major.dd -- no sign (the field's floor is
// 0), no exponent. A single, non-backtracking pattern (one quantifier per
// group) -- not the trailing-zero-trimming shape sonar flagged before.
const MONEY_FORMAT = /^\d+(\.\d{1,2})?$/;

/** Parses a major-units money string (as typed into the device fee field)
 *  into integer minor units, or null when the input isn't a valid money
 *  amount (wrong shape, more than 2dp, negative). "1000" -> 100000,
 *  "1000.5" -> 100050, "1000.55" -> 100055, "1000.555" -> null. */
export function parseMajorMoneyToMinor(value: string): number | null {
  const trimmed = value.trim();
  if (!MONEY_FORMAT.test(trimmed)) return null;
  const [wholePart, fracPart = ''] = trimmed.split('.');
  const paddedFrac = `${fracPart}00`.slice(0, 2);
  return Number(wholePart) * 100 + Number(paddedFrac);
}

export function formStateFromConfig(config: DashboardConfig): ConfigFormState {
  return {
    tax_rate_bp: String(config.tax_rate_bp),
    tax_split_ngcb_gaming_levy_bp: String(config.tax_split_ngcb_gaming_levy_bp),
    tax_split_ngcb_lottery_dividend_bp: String(config.tax_split_ngcb_lottery_dividend_bp),
    tax_split_ngcb_community_benefit_bp: String(config.tax_split_ngcb_community_benefit_bp),
    tax_split_irc_bp: String(config.tax_split_irc_bp),
    commission_rate_bp: String(config.commission_rate_bp),
    licence_validity_days: String(config.licence_validity_days),
    device_monthly_fee_minor: minorToMajorString(config.device_monthly_fee_minor),
    sync_endpoint_url: config.sync_endpoint_url,
  };
}

/** "3000" -> "3000 bp = 30%"; a non-integer/out-of-range input yields no hint. */
export function bpPercentHint(value: string): string | null {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return null;
  // String(n / 100) already renders the shortest exact decimal for an
  // integer bp value ("30", "0.5", "0.05") - no trailing-zero trimming
  // needed, which kept a backtracking regex out of here (sonar S5852).
  return `${n} bp = ${String(n / 100)}%`;
}

function parseInt10(value: string): number | null {
  if (!/^-?\d+$/.test(value.trim())) return null;
  return Number(value.trim());
}

/** Server ranges (E2-API-REFERENCE.md Config, E2.1 spec 7.3): bp fields
 *  (including commission_rate_bp) 0-10000, licence days 1-365,
 *  device_monthly_fee_minor 0.00-1,000,000.00 major units entered with at
 *  most 2dp. */
export function validateField(field: EditableField, value: string): string | null {
  if (field === 'sync_endpoint_url') {
    const normalized = normalizeSyncEndpoint(value);
    return normalized === null ? 'Sync endpoint must be empty or a valid HTTP(S) URL without credentials, at most 255 characters.' : null;
  }
  if (field === 'device_monthly_fee_minor') {
    const minor = parseMajorMoneyToMinor(value);
    if (minor === null) return `${FIELD_LABELS[field]} must be a money amount with at most 2 decimal places.`;
    if (minor < 0 || minor > DEVICE_FEE_MAX_MINOR) {
      return `${FIELD_LABELS[field]} must be between 0.00 and ${minorToMajorString(DEVICE_FEE_MAX_MINOR)}.`;
    }
    return null;
  }
  const n = parseInt10(value);
  if (n === null) return `${FIELD_LABELS[field]} must be a whole number.`;
  if (field === 'licence_validity_days') {
    if (n < 1 || n > 365) return 'Licence validity must be between 1 and 365 days.';
    return null;
  }
  if (n < 0 || n > 10000) return `${FIELD_LABELS[field]} must be between 0 and 10000.`;
  return null;
}

/** Normalize a user-entered endpoint to the same canonical form as the server. */
export function normalizeSyncEndpoint(raw: string): string | null {
  let value = raw.trim();
  while (value.endsWith('/')) value = value.slice(0, -1);
  if (value === '') return '';
  if (value.length > 255) return null;
  try {
    const url = new URL(value);
    if ((url.protocol !== 'http:' && url.protocol !== 'https:') || url.username !== '' || url.password !== '') {
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

export function validateConfigSection(form: ConfigFormState, section: ConfigSection): string | null {
  for (const field of CONFIG_SECTION_FIELDS[section]) {
    const problem = validateField(field, form[field]);
    if (problem) return problem;
  }
  return null;
}

/** Only the fields whose parsed value actually differs from the loaded config. */
export function buildConfigDiff(original: DashboardConfig, form: ConfigFormState): UpdateConfigRequest {
  const diff: UpdateConfigRequest = {};
  for (const field of [...BP_FIELDS, 'licence_validity_days'] as const) {
    const n = Number(form[field]);
    if (n !== original[field]) diff[field] = n;
  }
  const feeMinor = parseMajorMoneyToMinor(form.device_monthly_fee_minor);
  if (feeMinor !== null && feeMinor !== original.device_monthly_fee_minor) {
    diff.device_monthly_fee_minor = feeMinor;
  }
  const endpoint = normalizeSyncEndpoint(form.sync_endpoint_url);
  if (endpoint !== null && endpoint !== original.sync_endpoint_url) {
    diff.sync_endpoint_url = endpoint;
  }
  return diff;
}

export function buildSectionConfigDiff(
  original: DashboardConfig,
  form: ConfigFormState,
  section: ConfigSection,
): UpdateConfigRequest {
  const diff = buildConfigDiff(original, form);
  return Object.fromEntries(
    CONFIG_SECTION_FIELDS[section]
      .filter((field) => field in diff)
      .map((field) => [field, diff[field]]),
  ) as UpdateConfigRequest;
}
